import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IServerOrder } from '@rbi-ctg/menu';
import { DeliveryStatus, RbiOrderStatus, useGetOrderLazyQuery } from 'generated/graphql-gateway';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { usePrevious } from 'hooks/use-previous';
import { OrderSuccessFailureStatuses } from 'state/order/constants';
import { orderPollFailure, orderPollSuccessful } from 'state/order/order-state-utils';
import { logger } from 'utils/logger';
import noop from 'utils/noop';

import { handleOrderStatusChange } from './on-order-status-change';
import { IUseOrderStatusParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1500;
// stop polling after 50 calls (50 * 1.5s = 75s)
const MAX_POLLING_CALLS = 50;

export const useOrderStatus = ({
  failureStatuses = OrderSuccessFailureStatuses.failures,
  onSuccess = noop,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  rbiOrderId,
  successStatuses = OrderSuccessFailureStatuses.success,
  skip,
}: IUseOrderStatusParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pollingFailed, setPollingFailed] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingCallCountRef = useRef(0);
  const serverOrderRef = useRef<IServerOrder | null>(null);

  // Use lazy query instead of regular query
  // This is to solve an issue where apollo would not stop polling
  // after we called stopPolling
  const [getOrder, getOrderResult] = useGetOrderLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const { data } = getOrderResult;

  const serverOrder = useMemo<IServerOrder | null>(
    // FIXME: Do not alias the order with IServerOrder, as this is prone to masking schema errors
    () => (data?.order ? ((data.order as unknown) as IServerOrder) : null),
    [data]
  );

  const orderStatus = serverOrder?.status ?? null;
  const orderDeliveryStatus = serverOrder?.delivery?.status ?? null;

  serverOrderRef.current = serverOrder;

  const prevOrderStatus = usePrevious<RbiOrderStatus | null>(orderStatus);
  const prevOrderDeliveryStatus = usePrevious<DeliveryStatus | null>(orderDeliveryStatus);

  const isOrderErrorStatus =
    serverOrder?.rbiOrderId === rbiOrderId &&
    orderPollFailure({
      deliveryFailureStatus: failureStatuses.delivery,
      order: serverOrder,
      orderFailureStatus: failureStatuses.pos,
    });

  const isOrderIdValid =
    !serverOrder?.rbiOrderId || (rbiOrderId && serverOrder?.rbiOrderId === rbiOrderId);

  const isOrderSuccessStatus =
    serverOrder?.rbiOrderId === rbiOrderId &&
    orderPollSuccessful({
      deliverySuccessStatus: successStatuses.delivery,
      order: serverOrder,
      orderSuccessStatus: successStatuses.pos,
    });

  const executeQuery = useCallback(async () => {
    // rbiOrderId can be `null`
    if (rbiOrderId) {
      await getOrder({ variables: { id: rbiOrderId } });
    }
  }, [getOrder, rbiOrderId]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    pollingIntervalRef.current = null;
    pollingCallCountRef.current = 0;
  }, []);

  useEffect(() => {
    if (!isOrderIdValid) {
      stopPolling();
    }
  }, [isOrderIdValid, stopPolling]);

  useEffect(() => {
    if (isOrderSuccessStatus || isOrderErrorStatus) {
      setIsLoading(false);
      stopPolling();
    }
  }, [isOrderSuccessStatus, isOrderErrorStatus, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      return;
    }

    setIsLoading(true);
    setPollingFailed(false);
    pollingCallCountRef.current = 0;

    executeQuery();

    pollingIntervalRef.current = setInterval(() => {
      executeQuery();
      pollingCallCountRef.current++;

      if (pollingCallCountRef.current >= MAX_POLLING_CALLS) {
        logger.error({
          message: `[useOrderStatus][startPolling]: Still polling for results after one minute. Stopping polling.`,
        });
        setPollingFailed(true);
        if (serverOrderRef.current) {
          setIsLoading(false);
        }
        stopPolling();
      }
    }, orderStatusPollInterval);
  }, [executeQuery, orderStatusPollInterval, stopPolling]);

  useEffect(() => {
    if (rbiOrderId && !skip) {
      startPolling();
    }
  }, [startPolling, rbiOrderId, skip]);

  useEffectOnUpdates(() => {
    if (!serverOrder) {
      return;
    }

    handleOrderStatusChange({
      failure: isOrderErrorStatus,
      onSuccess,
      orderStatus,
      orderDeliveryStatus,
      prevOrderStatus,
      prevOrderDeliveryStatus,
      serverOrder,
      success: isOrderSuccessStatus,
    });
  }, [
    isOrderErrorStatus,
    orderStatus,
    orderDeliveryStatus,
    prevOrderStatus,
    prevOrderDeliveryStatus,
    serverOrder,
    isOrderSuccessStatus,
  ]);

  useEffectOnUnmount(() => {
    stopPolling();
  });

  return useMemo(
    () => ({
      isLoading,
      failure: isOrderErrorStatus || pollingFailed,
      orderStatus,
      orderDeliveryStatus,
      prevOrderStatus,
      prevOrderDeliveryStatus,
      serverOrder,
      success: isOrderSuccessStatus,
      isPricingFailureRecoverable:
        !!serverOrder?.orderErrors?.orderInjectionErrors?.isTimeoutError ||
        !!serverOrder?.orderErrors?.orderInjectionErrors?.isUnknownError,
    }),
    [
      isLoading,
      isOrderErrorStatus,
      pollingFailed,
      orderStatus,
      orderDeliveryStatus,
      prevOrderStatus,
      prevOrderDeliveryStatus,
      serverOrder,
      isOrderSuccessStatus,
    ]
  );
};
